import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCodeIcon from '@mui/icons-material/QrCode';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  darken,
} from '@mui/material';
import { blue, grey, yellow } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import { useEvent } from 'hooks/useEvent';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { openExternal } from 'utils/openExternal';

export interface ShareEventProps {
  eventId: string;
  // uid: string;
}

export function ShareEvent({ eventId }: ShareEventProps) {
  const { t } = useTranslation();
  const [didCopy, setDidCopy] = useState(false);
  const [isVipShare, setIsVipShare] = useState(false);
  const qrCodeRef = useRef<HTMLDivElement | null>(null);
  const qrCodeDownloadSize = 1000;
  const { updateEvent, createVipKey, getEventUrl, eventIsExpired, event } = useEvent(eventId);
  const eventUrl = getEventUrl(isVipShare);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | Element>(null);
  const openMenu = Boolean(menuAnchorEl);

  const handleOpenMenu = (ev: React.MouseEvent<Element>) => {
    ev.preventDefault();
    setMenuAnchorEl(ev.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleCopyUrl = () => {
    setDidCopy(false);
    navigator.clipboard.writeText(getEventUrl(isVipShare)).then(() => setDidCopy(true));
  };

  const handleDownloadQrCode = () => {
    if (event && qrCodeRef.current) {
      const svgEl = qrCodeRef.current.querySelector('svg');
      const svgXML = svgEl && new XMLSerializer().serializeToString(svgEl);

      if (svgXML) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgXML)}`;

        img.onload = () => {
          if (ctx) {
            canvas.width = qrCodeDownloadSize;
            canvas.height = qrCodeDownloadSize + 160;

            ctx.font = '64px Roboto';
            ctx.textAlign = 'center';
            ctx.fillText(t('qrCodeTitle', 'Request a song'), canvas.width / 2, 64);

            ctx.font = '32px Roboto';
            ctx.textAlign = 'right';
            ctx.fillText('dj-play.it', qrCodeDownloadSize, qrCodeDownloadSize + 136);

            if (event.expireDate) {
              ctx.font = '32px Roboto';
              ctx.textAlign = 'left';
              ctx.fillText(
                t('qrCodeOpenUntil', 'Requests open until {{date}} {{time}}', {
                  date: new Date(event.expireDate).toLocaleDateString(),
                  time: new Date(event.expireDate).toLocaleTimeString(),
                }),
                0,
                qrCodeDownloadSize + 136
              );
            }

            ctx.drawImage(img, 0, 96, qrCodeDownloadSize, qrCodeDownloadSize);

            const dataURL = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');

            downloadLink.href = dataURL;
            downloadLink.download = `event-${event.date}.png`;
            downloadLink.click();
          }
        };
      }
    }
  };

  const handleCreateVipKey = async () => {
    await updateEvent({
      vipKey: createVipKey(),
    });
  };

  const shareWhatsAppText = t(
    'whatsappShareText',
    'Please request your songs for *{{eventName}}* here:%0D%0A%0D%0A{{eventUrl}}',
    { eventName: event?.name, eventUrl }
  );

  const shareEmailText = t(
    'emailShareText',
    'Hello,%0D%0A%0D%0APlease request your songs for {{eventName}} here:%0D%0A%0D%0A{{eventUrl}}',
    { eventName: event?.name, eventUrl: encodeURIComponent(eventUrl) }
  );

  const shareEmailSubject = t('emailShareSubject', 'Your songs for {{eventName}}', {
    eventName: event?.name,
    eventUrl: encodeURIComponent(eventUrl),
  });

  useEffect(() => {
    if (event && !event.vipKey) {
      handleCreateVipKey();
    }
  }, []);

  return (
    <>
      <Box ref={qrCodeRef} sx={{ display: 'none' }}>
        <QRCode
          value={eventUrl}
          size={256}
          viewBox={`0 0 256 256`}
          style={{
            height: 'auto',
            maxWidth: '100%',
            margin: '0 auto',
            opacity: eventIsExpired ? 0.5 : 1,
          }}
        />
      </Box>
      <Stack
        spacing={2}
        sx={{
          p: 2,
          border: '2px solid',
          borderColor: (theme) => (isVipShare ? theme.palette.warning.main : 'transparent'),
          backgroundColor: (theme) =>
            isVipShare ? alpha(theme.palette.warning.light, 0.05) : 'transparent',
          borderRadius: 2,
          alignSelf: 'center',
        }}
      >
        <Stack direction="row" gap={2} justifyContent="center">
          <Tooltip
            title={t(
              'vipShareInfo',
              'Creates a share link for VIPs. VIPs can highlight important song requests and add a message to each request.'
            )}
          >
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  sx={{
                    '&.Mui-checked': (theme) => ({
                      color: theme.palette.warning.main,
                    }),
                  }}
                  value={isVipShare}
                  onChange={(e, checked) => {
                    console.log(checked);
                    setIsVipShare(checked);
                  }}
                />
              }
              label={t('vipShareCheckbox', 'Share for VIP')}
            />
          </Tooltip>
          <IconButton aria-label="settings" onClick={handleOpenMenu} component="a">
            <MoreVertRoundedIcon />
          </IconButton>
          <Menu anchorEl={menuAnchorEl} open={openMenu} onClose={handleCloseMenu}>
            <Tooltip
              title={t(
                'resetVipKeyInfo',
                'VIP share links will be invalidated and a new link will be created.'
              )}
            >
              <MenuItem onClick={handleCreateVipKey}>
                <ListItemText>{t('resetVipKey', 'Reset VIP link')}</ListItemText>
                <InfoOutlinedIcon sx={{ ml: 1 }} />
              </MenuItem>
            </Tooltip>
          </Menu>
        </Stack>
        <Divider sx={{ border: 0 }} />
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-evenly' }}>
          <Box textAlign="center">
            <IconButton
              onClick={handleCopyUrl}
              size="large"
              sx={{
                mb: 1,
                backgroundColor: blue[500],
                color: 'black',
                '&:hover': {
                  backgroundColor: blue[700],
                },
              }}
            >
              {didCopy ? <CheckIcon /> : <LinkIcon />}
            </IconButton>
            <Typography variant="subtitle2">{t('copyLink', 'Copy link')}</Typography>
          </Box>
          <Box textAlign="center">
            <IconButton
              onClick={() =>
                openExternal(`https://api.whatsapp.com/send?text=${shareWhatsAppText}`)
              }
              size="large"
              sx={{
                mb: 1,
                backgroundColor: '#25D366',
                color: 'black',
                '&:hover': {
                  backgroundColor: darken('#25D366', 0.2),
                },
              }}
            >
              <WhatsAppIcon />
            </IconButton>
            <Typography variant="subtitle2">{t('whatsapp', 'WhatsApp')}</Typography>
          </Box>
          <Box textAlign="center">
            <IconButton
              component="a"
              href={`mailto:?subject=${shareEmailSubject}&body=${shareEmailText}`}
              size="large"
              sx={{
                mb: 1,
                backgroundColor: yellow[500],
                color: 'black',
                '&:hover': {
                  backgroundColor: yellow[700],
                },
              }}
            >
              <MailOutlineIcon />
            </IconButton>
            <Typography variant="subtitle2">{t('email', 'Email')}</Typography>
          </Box>
          <Box textAlign="center">
            <IconButton
              onClick={handleDownloadQrCode}
              color="secondary"
              size="large"
              sx={{
                mb: 1,
                backgroundColor: grey[500],
                color: 'black',
                '&:hover': {
                  backgroundColor: grey[700],
                },
              }}
            >
              <QrCodeIcon />
            </IconButton>
            <Typography variant="subtitle2">{t('qrCode', 'QR Code')}</Typography>
          </Box>
        </Stack>
        <Divider>{t('or', 'or')}</Divider>
        <Box>
          <Typography gutterBottom sx={{ textAlign: 'center' }}>
            {t('copyLinkManually', 'Copy link manually')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <Box sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                value={eventUrl}
                size="small"
                slotProps={{
                  input: {
                    readOnly: true,
                    onKeyUp: (e) => e.currentTarget.select(),
                  },
                }}
                onFocus={(e) => e.target.select()}
              />
            </Box>
            <Box>
              <Button sx={{ height: '100%' }} onClick={() => openExternal(eventUrl)} size="small">
                <OpenInNewIcon />
              </Button>
            </Box>
          </Stack>
        </Box>
        {/* <Button fullWidth variant="outlined" onClick={handleDownloadQrCode}>
          Download QR-Code
        </Button> */}
      </Stack>
    </>
  );
}
